import React, { useState } from 'react';

import styled from '@emotion/styled';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Box } from '@clutter/clean';

import { NavLinkButton, Table } from '@shared/components/bootstrap';
import { Timestamp } from '@shared/components/helpers';

import { MultiselectFormControl } from '@admin/components/fields/multiselect_form_control';
import { Panel } from '@admin/components/helpers/panel';
import { Pagination } from '@admin/components/pagination';
import { Spinner } from '@admin/components/spinner';
import { trainingUploadAttestationsURL, trainingUploadEditURL, trainingUploadNewURL } from '@admin/config/routes';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { TRAINING_UPLOAD, TEAM } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  TrainingUploadsPaginatedQuery,
  useTrainingUploadsPaginatedQuery,
  useTrainingUploadTeamsQuery,
} from '@admin/schema';

import { Label } from '../helpers/label';

type TrainingUploadRow = NonNullable<TrainingUploadsPaginatedQuery['paginated']>['results'][number];

const TeamLabels = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
`;

const TextLink = styled(NavLinkButton)`
  color: teal;
  align-self: center;
`;

const AttachmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

function formatContentType(contentType?: string | null): string {
  if (!contentType) return 'File';
  if (contentType.startsWith('image/')) return 'Image';
  if (contentType.startsWith('video/')) return 'Video';
  if (contentType === 'application/pdf') return 'PDF';
  return 'File';
}

export const TrainingUploadList: React.FC = () => {
  useTitle('Training Uploads');
  const canCreateTrainingUploads = usePermission(TRAINING_UPLOAD.resource, TRAINING_UPLOAD.actions.create);
  const canUpdateTrainingUploads = usePermission(TRAINING_UPLOAD.resource, TRAINING_UPLOAD.actions.update);
  const canReadTeams = usePermission(TEAM.resource, TEAM.actions.read);
  const [page, setPage] = useState<number | undefined>();
  const [teamIDs, setTeamIDs] = useState<string[]>([]);
  const [from, setFrom] = useState<string | undefined>();
  const [till, setTill] = useState<string | undefined>();
  const { data, loading, error } = useTrainingUploadsPaginatedQuery({
    client,
    variables: {
      page,
      teamIDs: teamIDs.length > 0 ? teamIDs : undefined,
      from: from || undefined,
      till: till || undefined,
    },
    fetchPolicy: 'network-only',
  });
  const { data: teamsData } = useTrainingUploadTeamsQuery({
    client,
    skip: !canReadTeams,
  });
  const paginated = data?.paginated;
  const teams = teamsData?.teams ?? [];

  return (
    <>
      <Panel>
        <Panel.Body>
          <div className="row">
            {canReadTeams && (
              <div className="col-md-4">
                <div className="form-group">
                  <label className="control-label">Teams:</label>
                  <MultiselectFormControl
                    options={teams}
                    selectedIDs={teamIDs}
                    onChange={(ids) => {
                      setTeamIDs(ids);
                      setPage(undefined);
                    }}
                    placeholder="All Teams"
                  />
                </div>
              </div>
            )}
            <div className={canReadTeams ? 'col-md-4' : 'col-md-6'}>
              <div className="form-group">
                <label className="control-label" htmlFor="uploadedFrom">
                  Uploaded From:
                </label>
                <input
                  id="uploadedFrom"
                  min="0000-01-01"
                  max="9999-12-31"
                  type="date"
                  className="form-control"
                  value={from || ''}
                  onChange={(event) => {
                    setFrom(event.target.value || undefined);
                    setPage(undefined);
                  }}
                />
              </div>
            </div>
            <div className={canReadTeams ? 'col-md-4' : 'col-md-6'}>
              <div className="form-group">
                <label className="control-label" htmlFor="uploadedTill">
                  Uploaded Till:
                </label>
                <input
                  id="uploadedTill"
                  min="0000-01-01"
                  max="9999-12-31"
                  type="date"
                  className="form-control"
                  value={till || ''}
                  onChange={(event) => {
                    setTill(event.target.value || undefined);
                    setPage(undefined);
                  }}
                />
              </div>
            </div>
          </div>
        </Panel.Body>
      </Panel>
      <Panel>
        <Table responsive striped>
          <thead>
            <tr>
              <th>Name</th>
              <th>Files</th>
              <th>Quiz</th>
              <th>Teams</th>
              <th>Uploader</th>
              <th>Uploaded At</th>
              <th>Archived At</th>
              <th className="col-sm-1 text-right">
                {canCreateTrainingUploads && (
                  <NavLinkButton to={trainingUploadNewURL()} kind="mint">
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </NavLinkButton>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginated?.results.map((trainingUpload: TrainingUploadRow) => (
              <tr key={trainingUpload.id}>
                <td>
                  <strong>{trainingUpload.name}</strong>
                </td>
                <td>
                  <AttachmentList>
                    {trainingUpload.attachments.map((attachment) => (
                      <TextLink key={attachment.id} to={attachment.url} target="_blank">
                        {attachment.filename} ({formatContentType(attachment.contentType)})
                      </TextLink>
                    ))}
                  </AttachmentList>
                </td>
                <td>
                  {trainingUpload.requiresQuiz ? (
                    <Label kind="info">
                      {trainingUpload.questionsCount} Question{trainingUpload.questionsCount !== 1 ? 's' : ''}
                    </Label>
                  ) : (
                    <Label kind="default">Attestation Only</Label>
                  )}
                </td>
                <td>
                  <TeamLabels>
                    {trainingUpload.teams.map((team) => (
                      <Label kind="default" key={team.id}>
                        {team.name}
                      </Label>
                    ))}
                  </TeamLabels>
                </td>
                <td>{trainingUpload.uploader.name}</td>
                <td>{<Timestamp value={trainingUpload.createdAt} format={Timestamp.Format.DateTime} />}</td>
                <td>
                  {trainingUpload.deletedAt && (
                    <Timestamp value={trainingUpload.deletedAt} format={Timestamp.Format.DateTime} />
                  )}
                </td>
                <td>
                  {canUpdateTrainingUploads && (
                    <NavLinkButton kind="warning" to={trainingUploadEditURL(trainingUpload.id)}>
                      Edit
                    </NavLinkButton>
                  )}
                  <Box margin={'8px 0 0 0'}>
                    <NavLinkButton kind="primary" to={trainingUploadAttestationsURL(trainingUpload.id)}>
                      View Attestations
                    </NavLinkButton>
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {loading && <Spinner />}
        {error && (
          <div className="alert alert-danger" role="alert">
            Failed to load training uploads
          </div>
        )}
        {paginated && <Pagination pagination={paginated.pagination} onPage={setPage} />}
      </Panel>
    </>
  );
};
